import React, { useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { selectUserRoles } from '../../../features/auth/authSlice';

const RoleBasedWelcomeMessage = () => {
  const userRoles = useSelector(selectUserRoles, shallowEqual);

  // Memoize welcome message calculation to prevent re-computation on every render
  const welcomeMessage = useMemo(() => {
    if (userRoles.includes('Admin')) {
      return 'System Administration Dashboard';
    } else if (userRoles.includes('Claims_Officer')) {
      return 'Claims Processing Center';
    } else if (userRoles.includes('Approver')) {
      return 'Claims Approval Center';
    } else if (userRoles.includes('Finance')) {
      return 'Finance Dashboard';
    } else if (userRoles.includes('HR')) {
      return 'HR Claims Portal';
    } else if (userRoles.includes('Broker')) {
      return 'Broker Claims Portal';
    } else if (userRoles.includes('CRM')) {
      return 'CRM Portal';
    }
    return 'Corporate Claims Portal';
  }, [userRoles]);

  // Memoize sub message calculation
  const subMessage = useMemo(() => {
    if (userRoles.includes('Admin')) {
      return 'Complete system oversight and configuration management';
    } else if (userRoles.includes('Claims_Officer')) {
      return 'Review and process submitted claims with workflow efficiency';
    } else if (userRoles.includes('Approver')) {
      return 'Review recommendations and authorize claim decisions';
    } else if (userRoles.includes('Finance')) {
      return 'Financial oversight and payment processing';
    } else if (userRoles.includes('HR')) {
      return 'Employee claims management and HR workflows';
    } else if (userRoles.includes('Broker')) {
      return 'Client claims submission and status tracking';
    } else if (userRoles.includes('CRM')) {
      return 'Customer relationship and communication management';
    }
    return 'Comprehensive claims management platform';
  }, [userRoles]);

  // Memoize role indicator calculation
  const roleIndicator = useMemo(() => {
    if (userRoles.includes('Admin')) {
      return { color: 'bg-deep-jungle', label: 'ADMIN' };
    } else if (userRoles.includes('Claims_Officer')) {
      return { color: 'bg-fresh-leaf', label: 'CLAIMS' };
    } else if (userRoles.includes('Approver')) {
      return { color: 'bg-forest-fern', label: 'APPROVAL' };
    } else if (userRoles.includes('Finance')) {
      return { color: 'bg-zesty-lime', label: 'FINANCE' };
    } else if (userRoles.includes('HR')) {
      return { color: 'bg-woodland-moss', label: 'HR' };
    } else if (userRoles.includes('Broker')) {
      return { color: 'bg-steel', label: 'BROKER' };
    } else if (userRoles.includes('CRM')) {
      return { color: 'bg-graphite', label: 'CRM' };
    }
    return { color: 'bg-steel', label: 'USER' };
  }, [userRoles]);

  return (
    <div className="space-y-3">
      <div className="flex items-center space-x-4">
        <div 
          className={`${roleIndicator.color} text-crystal-white px-3 py-1 text-xs font-mono font-bold tracking-wider`}
          style={{ borderRadius: '8px' }}
        >
          {roleIndicator.label}
        </div>
        <div className="h-4 w-px bg-steel opacity-30"></div>
        <h1 className="text-3xl lg:text-4xl font-display font-bold text-deep-jungle tracking-tight leading-tight">
          {welcomeMessage}
        </h1>
      </div>
      <p className="text-steel text-base lg:text-lg font-medium leading-relaxed max-w-4xl">
        {subMessage}
      </p>
    </div>
  );
};

export default React.memo(RoleBasedWelcomeMessage);
